/**
 * Storefront settings provider — store details, theme, navigation, categories.
 *
 * Fetched once and shared, because the header, footer, menus and every listing
 * page need the same data. The theme is applied and cached on arrival so a
 * repeat visit paints in the right colours before React mounts.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, type StorefrontResponse } from './api';
import { applyTheme, cacheTheme } from './theme';

interface StoreContextValue {
  data: StorefrontResponse | null;
  loading: boolean;
  error: string | null;
  categoryBySlug: (slug: string) => StorefrontResponse['categories'][number] | undefined;
}

const StoreContext = createContext<StoreContextValue | null>(null);

export function StoreProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<StorefrontResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    void api
      .storefront()
      .then((response) => {
        if (cancelled) return;
        setData(response);
        applyTheme(response.theme);
        cacheTheme(response.theme);
      })
      .catch(() => {
        if (!cancelled) {
          setError(
            'We could not load the shop just now. Please refresh, or try again shortly.',
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo<StoreContextValue>(
    () => ({
      data,
      loading,
      error,
      categoryBySlug: (slug) => data?.categories.find((c) => c.slug === slug),
    }),
    [data, loading, error],
  );

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore(): StoreContextValue {
  const context = useContext(StoreContext);
  if (!context) throw new Error('useStore must be used inside <StoreProvider>');
  return context;
}
